'use client';

import { useQuery } from '@tanstack/react-query';
import { estimateOperationCosts, type EstimateLine } from '../actions/estimateOperationCost';
import { DEFAULT_OPERATIONS } from '../types/config';

export type { EstimateLine };

/**
 * Client seam for the "~N tokens" preview: prices prospective ops against ACCOUNTS
 * (POST /billing/estimate) via the `estimateOperationCosts` server action, so the
 * shown estimate equals the real charge instead of a portal-local sell-rate (#79).
 *
 * - Cached briefly (60s staleTime, keyed on the exact lines) → repeated identical
 *   inputs reuse the same estimate instead of re-hitting accounts.
 * - While loading / on failure it falls back to the FLAT per-op-type reservation from
 *   the operations registry, so the preview never blanks and never blocks the UX.
 *   (The server action itself already flat-falls-back per line; this covers the
 *   loading window and a total RPC failure.)
 *
 * Returns per-line costs in the SAME order as `lines`. TTS lines come back per-block
 * (the caller multiplies by block count); imagen lines are per the `imageCount` given.
 */
export function useOperationCosts(lines: EstimateLine[]): { costs: number[]; loading: boolean } {
  const key = JSON.stringify(lines);
  const { data, isLoading } = useQuery({
    queryKey: ['ai', 'op-cost-estimate', key],
    queryFn: () => estimateOperationCosts(lines),
    staleTime: 60_000,
    enabled: lines.length > 0,
  });

  const fallback = lines.map((l) => DEFAULT_OPERATIONS[l.opType]?.reservation ?? 1);
  return { costs: data ?? fallback, loading: isLoading };
}
